<template lang="pug">
    full-height-drawer(
        :visible.sync="localVisible"
        :title="isConnected ? $t('Edit WhatsApp account') : $t('New WhatsApp account')"
        size="520px"
        destroy-on-close
        @open="onOpen"
        @close="onClose"
    )
        el-form(label-position="top" :model="form" :rules="rules" ref="form")
            el-form-item(:label="$t('Name')" prop="name")
                el-input(v-model="form.name" :disabled="!canEdit" :placeholder="$t('e.g. Shop WhatsApp')")

            .channel-card(:class="{ 'is-connected': isConnected && !connectionInterrupted, 'is-interrupted': connectionInterrupted }")
                .channel-card__logo
                    i.el-icon-fa-whatsapp.fab
                .channel-card__body
                    .channel-card__title WhatsApp
                    .channel-card__status
                        span.dot
                        span {{ connectionStatusLabel }}
                    .channel-card__number(v-if="form.from_address") {{ form.from_address }}
                //- Once connected, reconnecting is a quiet secondary action on the card —
                //- not a dominant full-width button.
                el-button.channel-card__reconnect(
                    v-if="isConnected && canConnect && canCreateAccounts"
                    type="text" size="mini"
                    icon="el-icon-fa-arrows-rotate fas"
                    :loading="connecting"
                    @click="launchSignup"
                ) {{ $t('Reconnect') }}

            //- First-time connect: the prominent call to action + the Meta explainer.
            template(v-if="!isConnected && canCreateAccounts")
                el-button.channel-connect(
                    v-if="!signupConfigLoaded || canConnect"
                    size="medium" type="success"
                    icon="el-icon-fa-whatsapp fab"
                    :loading="connecting"
                    :disabled="!canConnect"
                    @click="launchSignup"
                ) {{ $t('Connect with WhatsApp') }}

                .channel-note(v-if="signupConfigLoaded && !canConnect")
                    i.el-icon-fa-circle-info.fas
                    div
                        strong {{ $t('WhatsApp is not set up yet') }}
                        p {{ $t('The WhatsApp integration is not configured on this system. Please contact support.') }}

                p.help(v-if="canConnect") {{ $t('You will be redirected to Meta to log in and choose your WhatsApp number. We never see your password.') }}

        whats-app-template-manager.channel-templates(v-if="isConnected" ref="templates" :account-id="form.id")

        template(#footer-left)
            el-button(round size="mini" icon="el-icon-fa-times fas" @click="close") {{ $t('Cancel') }}
        template(#footer)
            el-button(
                v-if="isConnected && canUpdateAccounts"
                round size="mini" type="success"
                icon="el-icon-fa-save fas"
                :loading="saving"
                @click="submit"
            ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "New WhatsApp account": "Neues WhatsApp-Konto",
        "Edit WhatsApp account": "WhatsApp-Konto bearbeiten",
        "Name": "Name",
        "e.g. Shop WhatsApp": "z.B. Shop WhatsApp",
        "WhatsApp": "WhatsApp",
        "connected": "verbunden",
        "not connected": "nicht verbunden",
        "Connection interrupted": "Verbindung unterbrochen",
        "Connect with WhatsApp": "Mit WhatsApp verbinden",
        "Reconnect": "Neu verbinden",
        "WhatsApp is not set up yet": "WhatsApp ist noch nicht eingerichtet",
        "The WhatsApp integration is not configured on this system. Please contact support.": "Die WhatsApp-Integration ist auf diesem System nicht konfiguriert. Bitte wende dich an den Support.",
        "You will be redirected to Meta to log in and choose your WhatsApp number. We never see your password.": "Du wirst zu Meta weitergeleitet, um dich anzumelden und deine WhatsApp-Nummer auszuwählen. Wir sehen dein Passwort niemals.",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "Account saved": "Konto gespeichert",
        "WhatsApp connected": "WhatsApp verbunden",
        "Connection failed": "Verbindung fehlgeschlagen",
        "Could not save the account.": "Konto konnte nicht gespeichert werden."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useCommunicationStore } from '@/stores'
import { checkRestrictions } from '@/helpers'
import FullHeightDrawer from '@/components/FullHeightDrawer'
import WhatsAppTemplateManager from '@/extensions/communication/views/ChannelAccount/WhatsAppTemplateManager'
import { loadFacebookSdk, isMetaOrigin } from '@/extensions/communication/utils/facebookSdk'

const emptyForm = () => ({
    id: undefined,
    channel: 'whatsapp',
    name: '',
    from_address: '',
    is_validated: false,
    has_access_token: false
})

export default {
    name: 'ChannelAccountFormDrawer',
    components: {
        FullHeightDrawer,
        WhatsAppTemplateManager
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        account: {
            type: Object,
            default: null
        },
        // When the drawer is opened from a POS context, that point of sale is
        // pre-linked to the account through the Embedded Signup connect payload.
        pointOfSaleId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            form: emptyForm(),
            rules: {
                name: 'required'
            },
            saving: false,
            connecting: false,
            signupConfig: null,
            signupConfigLoaded: false,
            // Transient parts of one Embedded Signup attempt — the code (from FB.login)
            // and the session info (from the postMessage) arrive independently.
            signupCode: null,
            signupSession: null,
            sessionListener: null
        }
    },
    computed: {
        store() {
            return useCommunicationStore()
        },
        // Connecting (and reconnecting) is the create flag on ex:comm:accounts,
        // saving a connected account the update flag.
        canCreateAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'create')
        },
        canUpdateAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'update')
        },
        canEdit() {
            return this.isConnected ? this.canUpdateAccounts : this.canCreateAccounts
        },
        localVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            }
        },
        isConnected() {
            return !!_.get(this.form, 'id')
        },
        // Set up, but without a token Meta can be reached with (never issued, expired
        // unrenewed, or scrubbed in a local copy): reconnecting is the fix.
        connectionInterrupted() {
            return this.isConnected && !this.form.has_access_token
        },
        connectionStatusLabel() {
            if (this.connectionInterrupted) return this.$t('Connection interrupted')
            return this.isConnected ? this.$t('connected') : this.$t('not connected')
        },
        canConnect() {
            return !!_.get(this.signupConfig, 'configured')
        }
    },
    methods: {
        onOpen() {
            this.signupCode = null
            this.signupSession = null

            if (this.account) {
                this.form = _.merge(emptyForm(), _.pick(_.cloneDeep(this.account), [
                    'id', 'name', 'from_address', 'is_validated', 'has_access_token'
                ]))
            } else {
                this.form = emptyForm()
                // A new account is created through Embedded Signup (launchSignup), which
                // links the POS via the connect payload's point_of_sale_id — not here.
            }

            this.signupConfig = null
            this.signupConfigLoaded = false
            this.store.whatsappSignupConfig()
                .then(config => { this.signupConfig = config })
                .catch(() => { this.signupConfig = null })
                .finally(() => { this.signupConfigLoaded = true })
        },
        onClose() {
            this.teardownListener()
        },
        // Launch Meta Embedded Signup: load the FB SDK, listen for the session info
        // (phone_number_id + waba_id) and run FB.login to get the authorization code.
        launchSignup() {
            if (!this.canConnect) return

            this.connecting = true
            this.signupCode = null
            this.signupSession = null

            loadFacebookSdk({ appId: this.signupConfig.app_id, version: this.signupConfig.graph_version })
                .then(FB => {
                    this.attachListener()
                    FB.login(response => {
                        const code = _.get(response, 'authResponse.code')
                        if (code) {
                            this.signupCode = code
                            this.tryFinishConnect()
                        } else {
                            // User closed/cancelled the popup.
                            this.connecting = false
                            this.teardownListener()
                        }
                    }, {
                        config_id: this.signupConfig.config_id,
                        response_type: 'code',
                        override_default_response_type: true,
                        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' }
                    })
                })
                .catch(() => {
                    this.connecting = false
                    this.notifyError(this.$t('Connection failed'))
                })
        },
        attachListener() {
            this.teardownListener()
            this.sessionListener = event => {
                if (!isMetaOrigin(event.origin)) return
                let data
                try {
                    data = JSON.parse(event.data)
                } catch (error) {
                    return
                }
                if (_.get(data, 'type') === 'WA_EMBEDDED_SIGNUP' && _.get(data, 'event') === 'FINISH') {
                    this.signupSession = {
                        phone_number_id: _.get(data, 'data.phone_number_id'),
                        waba_id: _.get(data, 'data.waba_id')
                    }
                    this.tryFinishConnect()
                }
            }
            window.addEventListener('message', this.sessionListener)
        },
        teardownListener() {
            if (this.sessionListener) {
                window.removeEventListener('message', this.sessionListener)
                this.sessionListener = null
            }
        },
        // The code (FB.login) and session info (postMessage) arrive in any order;
        // finalize once we have both.
        tryFinishConnect() {
            if (!this.signupCode || !this.signupSession) return

            const payload = {
                code: this.signupCode,
                phone_number_id: this.signupSession.phone_number_id,
                waba_id: this.signupSession.waba_id
            }
            if (this.pointOfSaleId) payload.point_of_sale_id = this.pointOfSaleId
            if (this.form.name) payload.name = this.form.name

            this.teardownListener()
            this.signupCode = null
            this.signupSession = null

            this.store.connectWhatsApp(payload)
                .then(account => {
                    this.form = _.merge(emptyForm(), _.pick(account, ['id', 'name', 'from_address', 'is_validated', 'has_access_token']))
                    this.notifySuccess(this.$t('WhatsApp connected'))
                    this.$emit('saved')
                })
                .catch(() => this.notifyError(this.$t('Connection failed')))
                .finally(() => { this.connecting = false })
        },
        // Opened with a preset: the template manager starts on a prefilled form
        // rather than a blank one (see the ticket-template section).
        openTemplatePreset(preset) {
            const manager = this.$refs.templates;
            if (manager && manager.startFromPreset) manager.startFromPreset(preset)
        },
        submit() {
            this.$refs.form.validate(valid => {
                if (!valid) return

                this.saving = true
                this.store.saveAccount({
                    id: this.form.id,
                    name: this.form.name
                })
                    .then(() => {
                        this.notifySuccess(this.$t('Account saved'))
                        this.$emit('saved')
                    })
                    .catch(() => this.notifyError(this.$t('Could not save the account.')))
                    .finally(() => this.saving = false)
            })
        },
        close() {
            this.localVisible = false
        },
        notifySuccess(title) {
            this.$notify.success({
                title,
                iconClass: 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        },
        notifyError(title) {
            this.$notify.error({
                title,
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        }
    },
    beforeDestroy() {
        this.teardownListener()
    }
}
</script>

<style lang="sass" scoped>
.channel-card
    display: flex
    align-items: center
    gap: 14px
    padding: 16px
    border: 1px solid #EBEEF5
    border-radius: 10px
    background: #FAFAFA
    margin-bottom: 14px

    &__logo
        flex: 0 0 auto
        width: 46px
        height: 46px
        border-radius: 12px
        display: flex
        align-items: center
        justify-content: center
        background: rgba(103, 194, 58, .12)
        color: #67C23A
        font-size: 24px

    &__body
        flex: 1 1 auto
        min-width: 0

    &__title
        font-weight: 600
        color: #303133
        line-height: 1.2

    &__status
        display: flex
        align-items: center
        gap: 7px
        margin-top: 4px
        font-size: .85rem
        color: #909399

        .dot
            width: 8px
            height: 8px
            border-radius: 50%
            background: #C0C4CC

    &__number
        margin-top: 3px
        font-size: .85rem
        color: #606266

    &__reconnect
        flex: 0 0 auto
        margin-left: auto
        align-self: center
        color: #909399
        &:hover
            color: #67C23A

    &.is-connected
        border-color: rgba(103, 194, 58, .4)
        background: rgba(103, 194, 58, .06)

        .channel-card__status
            color: #67C23A

            .dot
                background: #67C23A

    &.is-interrupted
        border-color: rgba(230, 162, 60, .4)
        background: rgba(230, 162, 60, .06)

        .channel-card__status
            color: #E6A23C

            .dot
                background: #E6A23C

.channel-connect
    width: 100%

// The templates block is its own section — separate it from the connection area with
// a hairline + breathing room so the two don't read as one crammed cluster.
.channel-templates
    display: block
    margin-top: 4px
    padding-top: 20px
    border-top: 1px solid #EBEEF5

.channel-note
    display: flex
    gap: 10px
    margin-top: 14px
    padding: 12px 14px
    border-radius: 8px
    background: #F4F4F5
    color: #909399

    i
        margin-top: 2px
        font-size: 1rem

    strong
        display: block
        margin-bottom: 3px
        color: #606266

    p
        margin: 0
        line-height: 1.4
        font-size: .85rem

.help
    margin: 10px 2px 0
    line-height: 1.4
    font-size: .8rem
    color: #909399
</style>
